// src/hooks/usePhotoCaptions.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import { getBackendPhotoId, isRealBackendPhotoId } from './useParentPhotos';

export default function usePhotoCaptions(projectId, backendPhotos = [], options = {}) {
  const [captionsByPhotoId, setCaptionsByPhotoId] = useState({});
  const [fetchingCaptionIds, setFetchingCaptionIds] = useState({});
  const requestedCaptionIdsRef = useRef(new Set());

  // Some gallery APIs already return the caption with each media item.
  // In that case callers can disable the per-photo GET request and seed
  // caption state directly from a field such as `original_caption`.
  const fetchCaptions = options?.fetchCaptions !== false;
  const initialCaptionField = options?.initialCaptionField || null;

  const backendPhotoIds = useMemo(
    () => backendPhotos.map(getBackendPhotoId).filter(isRealBackendPhotoId),
    [backendPhotos]
  );

  useEffect(() => {
    requestedCaptionIdsRef.current = new Set();
    setCaptionsByPhotoId({});
    setFetchingCaptionIds({});
  }, [projectId]);

  useEffect(() => {
    if (!projectId || backendPhotoIds.length === 0) return;

    // MediaGallery already receives `original_caption` in mediaList, so it can
    // opt out of api.getCaption() entirely. Only seed captions we have not
    // changed locally yet, so a later mediaList refresh cannot overwrite an
    // edit that was just saved by the user.
    if (!fetchCaptions) {
      if (!initialCaptionField) return;

      setCaptionsByPhotoId((prev) => {
        let changed = false;
        const next = { ...prev };

        backendPhotos.forEach((photo) => {
          const photoId = getBackendPhotoId(photo);
          if (!isRealBackendPhotoId(photoId)) return;
          requestedCaptionIdsRef.current.add(photoId);

          if (!Object.prototype.hasOwnProperty.call(prev, photoId)) {
            next[photoId] = photo?.[initialCaptionField] || '';
            changed = true;
          }
        });

        return changed ? next : prev;
      });

      return;
    }

    let cancelled = false;

    backendPhotoIds.forEach(async (photoId) => {
      if (!isRealBackendPhotoId(photoId)) return;
      if (requestedCaptionIdsRef.current.has(photoId)) return;

      requestedCaptionIdsRef.current.add(photoId);
      setFetchingCaptionIds((prev) => ({ ...prev, [photoId]: true }));

      try {
        const res = await api.getCaption(projectId, photoId);
        if (!cancelled) {
          setCaptionsByPhotoId((prev) => ({
            ...prev,
            [photoId]: res?.caption_override || res?.caption || '',
          }));
        }
      } catch (_error) {
        if (!cancelled) {
          setCaptionsByPhotoId((prev) => ({ ...prev, [photoId]: '' }));
        }
      } finally {
        if (!cancelled) {
          setFetchingCaptionIds((prev) => ({ ...prev, [photoId]: false }));
        }
      }
    });

    return () => {
      cancelled = true;
    };
  }, [backendPhotoIds, backendPhotos, fetchCaptions, initialCaptionField, projectId]);

  const deleteCaption = useCallback(
    async (photoId) => {
      if (!projectId || !isRealBackendPhotoId(photoId)) {
        return { ok: false, error: new Error('projectId and a backend photo id are required') };
      }

      try {
        await api.deleteCaption(projectId, photoId);
        setCaptionsByPhotoId((prev) => ({ ...prev, [photoId]: '' }));
        requestedCaptionIdsRef.current.add(photoId);
        return { ok: true };
      } catch (error) {
        console.error('Failed to delete caption:', error);
        return { ok: false, error };
      }
    },
    [projectId]
  );

  const saveCaption = useCallback(
    async (photoId, caption) => {
      if (!projectId || !isRealBackendPhotoId(photoId)) {
        return { ok: false, error: new Error('projectId and a backend photo id are required') };
      }

      const finalCaption = (caption || '').trim();
      const existingCaption = captionsByPhotoId[photoId] || '';

      try {
        if (!finalCaption) {
          if (existingCaption) {
            await api.deleteCaption(projectId, photoId);
          }
        } else if (existingCaption) {
          await api.updateCaption(projectId, photoId, finalCaption);
        } else {
          await api.createCaption(projectId, photoId, finalCaption);
        }

        setCaptionsByPhotoId((prev) => ({ ...prev, [photoId]: finalCaption }));
        requestedCaptionIdsRef.current.add(photoId);
        return { ok: true, caption: finalCaption };
      } catch (error) {
        console.error('Failed to save caption:', error);
        return { ok: false, error };
      }
    },
    [captionsByPhotoId, projectId]
  );

  return {
    captionsByPhotoId,
    fetchingCaptionIds,
    saveCaption,
    deleteCaption,
  };
}
